import React, { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { useNavigationType } from 'react-router';
import { useDebouncedCallback } from 'use-debounce';

import {
  BtnBar,
  DangerAlert,
  InfoAlert,
  SecondaryBtn,
  SuccessAlert,
  Table,
  WaitingAlert,
  WarningAlert,
} from '@tmapy/style-guide';

import { QUERY_STATE } from '../../constants';
import { useEpsiDocsListResource } from '../../openapi/operations/epsiDocsList';

import { PageTitle } from '../../app/components/PageTitle';
import { MainContent } from '../../app/components/MainContent';
import { LoadNextItemsBar } from '../../app/components/LoadNextItemsBar';
import { MapReviewButtons } from '../../docs/components/MapReviewButtons';
import { SSE_TYPE, useSSE } from '../../hooks';
import { SSE_REFETCH_DELAY, SSE_REFETCH_MAX_WAIT } from './constants';
import { EpsiListItem } from './EpsiListItem';
import { epsiMessages as msg } from './epsiMessages';
import { useEpsiAcl } from './useEpsiAcl';
import { useEpsiRetry } from './useEpsiRetry';
import {
  isEpsiSynchronizeAccepted,
  isEpsiSynchronizeConflict,
  isEpsiSynchronizeForbidden,
  useEpsiSynchronize,
} from './useEpsiSynchronize';

/** Zatím napevno, do konfigurace se vytáhne, až na to bude požadavek. */
const EPSI_MAP_URL = '/-/epsi';

export const Epsi: React.FC = () => {
  const navigationType = useNavigationType();
  const acl = useEpsiAcl();
  const { queryState, data, fetchMore, refetch } = useEpsiDocsListResource(
    {},
    navigationType !== 'PUSH',
  );
  // Synchronizace posílá zprávy v dávkách, refetch se proto slučuje.
  const refetchDebounced = useDebouncedCallback(refetch, SSE_REFETCH_DELAY, {
    maxWait: SSE_REFETCH_MAX_WAIT,
  });
  useSSE(SSE_TYPE.EPSI, undefined, refetchDebounced);

  const epsiList = data?.results ?? [];
  const epsiCount = data?.count;

  const {
    synchronize,
    queryState: synchronizeQueryState,
    status: synchronizeStatus,
    createdCount: synchronizeCreatedCount,
  } = useEpsiSynchronize(refetch);

  const { retry, retryStates, dismissRetry } = useEpsiRetry(refetch);

  const handleSynchronizeBtnClick = useCallback(() => {
    synchronize();
  }, [synchronize]);

  function handleNextEpsiBtnClick() {
    if (data?.next) {
      fetchMore(data.next);
    }
  }

  return (
    <MainContent scrollRestorationPrefix='epsi'>
      <div className='dmx-doc--head sg-u-vs-2'>
        <div className='sg-a-d-f sg-a-g-2 sg-a-ai-c sg-a-jc-sb sg-a-fw-w'>
          <header className='sg-a-d-f sg-a-ai-c sg-a-g-1 sg-a-fw-w'>
            <PageTitle>
              <FormattedMessage {...msg.title} />
            </PageTitle>
          </header>
          <BtnBar>
            {acl.epsiActionSynchronize && (
              <SecondaryBtn
                onClick={handleSynchronizeBtnClick}
                queryState={synchronizeQueryState === QUERY_STATE.WAITING ? 'waiting' : undefined}
              >
                <FormattedMessage {...msg.synchronizeBtn} />
              </SecondaryBtn>
            )}
            <MapReviewButtons reviewUrl={EPSI_MAP_URL} review3dUrl={null} />
          </BtnBar>
        </div>
        <p>
          <FormattedMessage {...msg.description} />
        </p>
        {isEpsiSynchronizeAccepted(synchronizeStatus) &&
          (synchronizeCreatedCount === 0 ? (
            <WarningAlert>
              <FormattedMessage {...msg.synchronizeUpToDate} />
            </WarningAlert>
          ) : (
            <SuccessAlert>
              <FormattedMessage {...msg.synchronizeStarted} />
            </SuccessAlert>
          ))}
        {isEpsiSynchronizeConflict(synchronizeStatus) && (
          <WarningAlert>
            <FormattedMessage {...msg.synchronizeRunning} />
          </WarningAlert>
        )}
        {isEpsiSynchronizeForbidden(synchronizeStatus) && (
          <DangerAlert>
            <FormattedMessage {...msg.synchronizeForbidden} />
          </DangerAlert>
        )}
        {synchronizeQueryState === QUERY_STATE.FAIL &&
          !isEpsiSynchronizeConflict(synchronizeStatus) &&
          !isEpsiSynchronizeForbidden(synchronizeStatus) && (
            <DangerAlert>
              <FormattedMessage {...msg.synchronizeFail} />
            </DangerAlert>
          )}
      </div>
      <article>
        <div>
          <div className='sg-u-vs-4'>
            <div className='sg-u-vs-4'>
              <section className='sg-u-vs-2'>
                {queryState === QUERY_STATE.WAITING && epsiList.length < 1 && (
                  <WaitingAlert>
                    <FormattedMessage {...msg.stateWaiting} />
                  </WaitingAlert>
                )}
                {queryState === QUERY_STATE.FAIL && epsiList.length < 1 && (
                  <DangerAlert>
                    <FormattedMessage {...msg.stateFail} />
                  </DangerAlert>
                )}
                {queryState === QUERY_STATE.SUCCESS && epsiList.length < 1 && (
                  <InfoAlert>
                    <FormattedMessage {...msg.stateEmpty} />
                  </InfoAlert>
                )}
                {epsiList.length > 0 && (
                  <>
                    <div className='sg-u-box sg-a-o-h dmx-a-mw-fc'>
                      <Table isClickable>
                        {/* Počet sloupců drží i EPSI_LIST_COLUMN_COUNT v constants.ts. */}
                        <thead>
                          <tr>
                            <th className='sg-a-ta-c'>
                              <FormattedMessage {...msg.columnState} />
                            </th>
                            <th className='sg-a-ta-c'>
                              <FormattedMessage {...msg.columnId} />
                            </th>
                            <th className='sg-a-ta-r'>
                              <FormattedMessage {...msg.columnPspiDatumOd} />
                            </th>
                            <th className='sg-a-ta-l'>
                              <FormattedMessage {...msg.columnType} />
                            </th>
                            <th className='sg-a-ta-r'>
                              <FormattedMessage {...msg.columnCreated} />
                              <>
                                <br />
                                <span className='dmx-u-c-cta-red'>
                                  <FormattedMessage {...msg.columnImported} />
                                </span>
                              </>
                            </th>

                            <th className='sg-a-ta-l'>
                              <FormattedMessage {...msg.columnSource} />
                            </th>
                            <th className='sg-a-ta-c'></th>
                            <th className='sg-a-ta-c'></th>
                          </tr>
                        </thead>
                        <tbody>
                          {epsiList.map((epsiItem) => (
                            <EpsiListItem
                              key={epsiItem.id}
                              data={epsiItem}
                              onRetry={acl.epsiActionRetry ? retry : undefined}
                              retryState={retryStates[epsiItem.id]}
                              onRetryDismiss={dismissRetry}
                            />
                          ))}
                        </tbody>
                      </Table>
                    </div>
                    <LoadNextItemsBar
                      count={epsiList.length}
                      total={epsiCount!}
                      queryState={queryState}
                      onNextBtnClick={handleNextEpsiBtnClick}
                    />
                  </>
                )}
              </section>
            </div>
          </div>
        </div>
      </article>
    </MainContent>
  );
};
